import payrollResource from "@/shared/resource/payroll";
import { authorizedAxios } from "./authorizedAxios";
import {
  PayrollInformation,
  EmployeeContributionsListResult,
  EmployeeContributionsUpdateResponse,
  ContributionParameters,
  EmployeeContributionRequest,
  EmployeeConributionHistorySummaryResponse,
  PlanDetailsInformation,
  PayrollSummaryToDate,
  PayrollContributionsGraph,
  EmployeePlanYearContributionResponse,
  Holiday,
  PayrollCalendarDate,
  PayrollValidationMessage,
  EmployeeSearchDropdownItem,
  FundingOptionRequest, PayrollReportDates
} from "@/employer/types";
import {
  ContributionMisc,
  PayrollContribution,
  UpdateRecurringContribution,
  PayrollUpdateResponse,
  PayrollSummaryWithPriorEmployer,
  OneTimePayrollContribution,
  OneTimeliftUpdateResponse,
  PayrollDateResponse,
  ScheduledPersonalContribution,
  PayrollSummaryPersonalScheduledContributions,
} from "@/shared/types";

export function savePayrollInformation (request: PayrollInformation) {
  return authorizedAxios.put<PayrollInformation>(payrollResource.payrollInformation(request.employerId), request)
    .then(response => {
      response.data.employerId = request.employerId;
      return response;
    });
}

export function savePayrollContactInformation (request: PayrollInformation) {
  return authorizedAxios.put<PayrollInformation>(payrollResource.payrollContactInformation(request.employerId), request.contact)
    .then(response => {
      response.data.employerId = request.employerId;
      return response;
    });
}

export function getPayrollInformation (employerId: number) {
  return authorizedAxios.get<PayrollInformation>(payrollResource.payrollInformation(employerId));
}
export function getPayrollReportDates (employerId: number, automatedLeaveDate: string) {
  return authorizedAxios.get<PayrollReportDates>(payrollResource.getPayrollReportDates(employerId, automatedLeaveDate));
}

export function downloadPayrollChangeReport (employerId: number, payrollDate: string) {
  return authorizedAxios.get(payrollResource.downloadPayrollChangeReport(employerId, payrollDate));
}

export function downloadPayrollFullReport (employerId: number, payrollDate: string) {
  return authorizedAxios.get(payrollResource.downloadPayrollFullReport(employerId, payrollDate));
}

export function getPayrollDates ({employerId, year}: { employerId: number, year: number}) {
  return authorizedAxios.get<PayrollCalendarDate[]>(payrollResource.payrollDates(employerId, year));
}

export function savePlanDetailInformation (employerId: number, request: PlanDetailsInformation) {
  return authorizedAxios.put<PlanDetailsInformation>(payrollResource.planDetail(employerId), request);
}

export function getPlanDetailInformation (employerId: number) {
  return authorizedAxios.get<PlanDetailsInformation>(payrollResource.planDetail(employerId));
}

export function getPayrollContributionsDashboardGraph (employerId: number, page: number) {
  return authorizedAxios.get<PayrollContributionsGraph>(payrollResource.getPayrollContributionsDashboardGraph(employerId, page));
}

export function getUserContribution () {
  return authorizedAxios.get(payrollResource.userContribution());
}

export function updateUserContribution (contribution: ContributionMisc) {
  return authorizedAxios.put(payrollResource.userContribution(), contribution);
}

export function getPredictedContributions () {
  return authorizedAxios.get<{contributions: PayrollContribution[]}>(payrollResource.getPredictedContributions());
}

export function updateRecurringEmployeeContribution (contributionUpdate: UpdateRecurringContribution) {
  return authorizedAxios.post<PayrollUpdateResponse>(payrollResource.updateRecurringEmployeeContribution(), contributionUpdate);
}

export function getUserPayrollSummary (username: string, partnerId: number) {
  const query = (username) ? { forUser: username, forUserPartnerId: partnerId } : undefined;
  return authorizedAxios.get<PayrollSummaryWithPriorEmployer>(payrollResource.getUserPayrollSummary(), query);
}

export function getUserPayrollSummaryPersonalScheduledContributions () {
  return authorizedAxios.get<PayrollSummaryPersonalScheduledContributions>(
    payrollResource.getUserPayrollSummaryPersonalScheduledContributions());
}

export function getUserLiftContribution (payrollDate: string) {
  return authorizedAxios.get<OneTimePayrollContribution>(payrollResource.getUserLiftContribution(payrollDate));
}

export function updateUserLiftContribution (payrollDate: string, amount: number) {
  return authorizedAxios.put<OneTimeliftUpdateResponse>(payrollResource.getUserLiftContribution(payrollDate), { amount });
}

export function getEmployeeContributions (contributionParameters: ContributionParameters) {
  return authorizedAxios.get<EmployeeContributionsListResult>(
    payrollResource.getEmployeeContributions(
      contributionParameters.pageSize,
      contributionParameters.pageNumber,
      contributionParameters.employerId,
      contributionParameters.searchTerm));
}

export function updateEmployeeContribution (payrollDate: string, employerId: number,
                                            request: EmployeeContributionRequest) {
  return authorizedAxios.put<EmployeeContributionsUpdateResponse>(
    payrollResource.updateEmployeeContribution(payrollDate, employerId), request);
}

export function getEmployeeYearToDatePayroll (employerId: number, employeeId: string) {
  return authorizedAxios.get<PayrollSummaryToDate>(
    payrollResource.getEmployeeYearToDatePayroll(employerId, employeeId));
}

export function getEmployerUpcomingPayrolls (employerId: number, noOfPayrolls: number) {
  return authorizedAxios.get(payrollResource.getEmployerUpcomingPayrolls(employerId, noOfPayrolls));
}

export function getPayrollDateAndCutoffDate (employerId: number) {
  return authorizedAxios.get(payrollResource.getPayrollDateAndCutoffDate(employerId));
}

export function getEmployeeYearToDateContribution (employerId: number, year: number) {
  return authorizedAxios.get<any>(
    payrollResource.getEmployeeContributionsByYear(employerId, year));
}

export function getEmployeePlanYearContributions (employerId: number, employeeId: number, pageNumber: number) {
  return authorizedAxios.get<EmployeePlanYearContributionResponse>(
    payrollResource.getEmployeePlanYearContributionsList(employerId, employeeId, pageNumber));
}

export function getEmployeePlanYearToDateContributionSummary (employerId: number, employeeId: number) {
  return authorizedAxios.get<EmployeeConributionHistorySummaryResponse>(
    payrollResource.getEmployeePlanYearToDateContributionSummary(employerId, employeeId));
}

export function getBankHolidaysForYear (year: number) {
  return authorizedAxios.get<{[year: number]: Holiday[]}>(
    payrollResource.getBankHolidaysInYear(year)
  );
}

export function getScheduledPersonalContribution () {
  return authorizedAxios.get<ScheduledPersonalContribution>(
    payrollResource.scheduledPersonalContribution()
  );
}

export function saveScheduledPersonalContribution (request: ScheduledPersonalContribution) {
  return authorizedAxios.put<ScheduledPersonalContribution>(
    payrollResource.scheduledPersonalContribution(), request
  );
}

export function saveEmployerPaydatesByYear (
  { employerId, year, paydates }: { employerId: number, year: number, paydates: PayrollCalendarDate[] }) {
  return authorizedAxios.put<PayrollCalendarDate[]>(
    payrollResource.payrollDates(employerId, year),
    paydates);
}

export function validateEmployerPaydatesByYear (
  { employerId, year, paydates }: { employerId: number, year: number, paydates: PayrollCalendarDate[] }) {
  return authorizedAxios.post<PayrollValidationMessage[]>(
    payrollResource.validateEmployerPaydatesByYear({employerId, year}),
    paydates); // FIXME ensure the signature here is correct!
}

export function saveFundingOption (employerId: number, request: FundingOptionRequest) {
  return authorizedAxios.put(payrollResource.fundingOption(employerId), request);
}

export function getOtherHsaCutOffDate () {
  return authorizedAxios.get<Date>(payrollResource.getOtherHsaCutOffDate());
}
